import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { useLocation } from "react-router-dom";

import { WEBSOCKET_CONFIG } from "./useWebSocket/config";
import { ConnectionState } from "./useWebSocket/types";
import { useWebSocketAuth } from "./useWebSocket/useWebSocketAuth";
import { WebSocketManager, WebSocketManagerState } from "./useWebSocket/WebSocketManager";

interface WebSocketContextState {
  manager: WebSocketManager | null;
  error: Error | null;
  connect: () => Promise<void>;
  connectionState: ConnectionState;
  isConnected: boolean;
  isConnecting: boolean;
  isDisconnected: boolean;
  disconnect: () => void;
  clearFilters: () => void;
  clearError: () => void;
}

const WebSocketContext = createContext<WebSocketContextState | undefined>(undefined);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const managerRef = useRef<WebSocketManager | null>(null);
  const connectRequestId = useRef(0);
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const [error, setError] = useState<Error | null>(null);
  const location = useLocation();

  const { getAuthenticatedUrl } = useWebSocketAuth();

  if (!managerRef.current) {
    managerRef.current = new WebSocketManager();
  }

  useEffect(() => {
    managerRef.current?.setUrlProvider(() => getAuthenticatedUrl(WEBSOCKET_CONFIG.url));
  }, [getAuthenticatedUrl]);

  const clearError = () => setError(null);

  const connect = useCallback(async () => {
    const manager = managerRef.current;
    if (!manager) return;

    const state = manager.getState();
    if (state === "connected" || state === "connecting") {
      return;
    }

    const currentRequestId = ++connectRequestId.current;

    try {
      const wsUrl = await getAuthenticatedUrl(WEBSOCKET_CONFIG.url);

      if (currentRequestId !== connectRequestId.current) {
        return;
      }

      if (manager.getState() === "connected" || manager.getState() === "connecting") {
        manager.disconnect();
      }

      manager.connect(wsUrl);
    } catch (err) {
      if (currentRequestId !== connectRequestId.current) {
        return;
      }

      console.error("Failed to get authenticated WebSocket URL:", err);
      const error = err instanceof Error ? err : new Error("Authentication failed");
      setError(error);
    }
  }, [getAuthenticatedUrl]);

  const disconnect = useCallback(() => {
    managerRef.current?.disconnect();
  }, []);

  const clearFilters = useCallback(() => {
    managerRef.current?.send(JSON.stringify({ clear: {} }));
  }, []);

  useEffect(() => {
    const manager = managerRef.current;
    if (!manager) return;
    const onError = (err: Error) => {
      console.log(error);
      setError(err);
    };
    const onConnected = () => {
      setError(null);
    };

    const onStateChange = (state: WebSocketManagerState) => {
      const connectionState: ConnectionState = state === "error" ? "error" : state;
      setConnectionState(connectionState);
    };

    manager.on("error", onError);
    manager.on("connected", onConnected);
    manager.on("stateChange", onStateChange);

    return () => {
      manager.disconnect();
    };
  }, []);

  const isConnected = useMemo(() => connectionState === "connected", [connectionState]);
  const isConnecting = useMemo(() => connectionState === "connecting", [connectionState]);
  const isDisconnected = useMemo(() => connectionState === "disconnected", [connectionState]);

  // Disconnect on route change because we don't want to keep the connection alive on routes without websockets, also helps with resetting config
  useEffect(() => {
    disconnect();
  }, [disconnect, location.pathname]);

  const value = {
    manager: managerRef.current,
    error,
    connectionState,
    isConnected,
    isConnecting,
    isDisconnected,
    connect,
    disconnect,
    clearFilters,
    clearError
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error("useWebSocketContext must be used within a WebSocketProvider");
  }
  return context;
};
